import publicConfig from '@/config/public'
import type { AnalyticsSession } from '@/libs/helpers/utils'

/**
 * First-party GA4 analytics, replacing the GTM container (APPSEC-64).
 *
 * Event names and params are copied 1:1 from the GTM container export
 * (GTM-M9CL6N4 "OO, CWA, and Login", workspace 13) so every existing GA4
 * funnel keeps working — event-name parity is the contract. The full
 * event → trigger → selector mapping lives in the hacks repo (gtag-swap/).
 *
 * The container let marketing attach arbitrary script to a payment page from
 * the GTM console; these explicit calls make the analytics surface reviewable
 * code and shrink the CSP to the path-pinned gtag.js loader.
 */

export type AnalyticsEvent =
  // pageview-driven (fired from route changes; see trackRouteView)
  | 'view_menu'
  | 'view_checkout'
  | 'order_placed'
  // funnel interactions
  | 'add_to_cart'
  | 'clear_cart'
  | 'proceed_to_checkout'
  | 'place_order'
  | 'purchase'
  // checkout auth modals
  | 'checkout_continue_as_guest'
  | 'checkout_sign_in'
  | 'checkout_create_account'
  | 'guest_checkout_continue'
  | 'guest_checkout_sign_in'
  | 'guest_checkout_create_account'
  | 'guest_checkout_finish_account'
  // digital gift card banner
  | 'dgc_banner_cta'

// gtag reads the dataLayer as pushed `arguments` objects; pushing an array
// literal does not register, hence the non-arrow function. The rest param
// exists only to type the call signature.
//
// The queue is created here rather than only in initAnalytics(): React runs
// child effects before parent effects, so a page's mount effect can fire an
// event before the provider tree has initialised. gtag.js drains whatever it
// finds in dataLayer when it loads, so queueing early is safe — whereas an
// optional-chained push would silently drop the event with no error and no
// failing test. That deterministically lost `purchase` on the hosted-payment
// return, where paymentstatus mounts as the initial page load.
//
// The try/catch keeps analytics out of checkout's blast radius: once gtag.js
// loads it instruments dataLayer.push, so a throw inside GA's own processing
// would otherwise propagate into the click handler that fired the event —
// including the place-order handler.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]): void {
  try {
    window.dataLayer = window.dataLayer ?? []
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  } catch {
    // Never surface an analytics failure to the guest.
  }
}

// The GTM container resolved shared params ({{venueXRefID}} etc.) from dataLayer
// variables that a `session` push kept current. This module keeps the same
// values here instead, merged into every event.
//
// venue_xref_id is deliberately NOT one of them: it is in the URL, which is the
// one value always correct for the page being viewed, so it is read at event
// time rather than cached and kept in step. The rest are venue configuration the
// URL cannot supply (fulfillment_type included -- it can be DOORDASH_DRIVE,
// which is not an order method). Those are tagged with the venue they describe
// and dropped once the guest is on a different one: sending no params beats
// sending another venue's.
let sessionParams: Record<string, unknown> = {}
let sessionVenueXRefID: string | undefined

export const initAnalytics = (): void => {
  if (!publicConfig.ga4MeasurementId) {
    return
  }
  gtag('js', new Date())
  // allow_google_signals:false stops the /ads/ga-audiences pixels to
  // www.google.<cctld> regardless of the GA-property-level Signals setting.
  gtag('config', publicConfig.ga4MeasurementId, { allow_google_signals: false })
}

// Mirrors the old dataLayer `session` push (same call sites), translated to
// the snake_case param names the GTM tags mapped the variables to.
export const setAnalyticsSession = (session: AnalyticsSession): void => {
  sessionVenueXRefID = session.venueXRefID
  sessionParams = {
    is_test: session.isTest,
    fulfillment_type: session.fulfillmentType,
    guest_checkout_available: session.guestCheckoutAvailable,
    is_guest_checkout: session.isGuestCheckout,
    is_scheduled_order: session.isScheduledOrder,
    has_menu_item_images: session.hasMenuItemImages,
    menu_item_images_pct: session.menuItemImagesPct,
    scheduled_ordering_available: session.scheduledOrderingAvailable,
    order_throttled: session.orderThrottled,
    payment_type: session.paymentType,
  }
}

// Session params ride on every event. The container omitted them on the
// checkout-modal events (empty settings tables); sending them uniformly is a
// deliberate, strictly-additive deviation — GA4 ignores unregistered params
// and event-name funnels are unaffected.
// Resolved per event rather than held in a variable, so a guest who moves
// between venues cannot carry the previous venue's params with them.
const analyticsContext = (): Record<string, unknown> => {
  const venueXRefID =
    typeof window === 'undefined' ? undefined : parseOORoute(window.location.pathname)?.venueXRefID
  if (venueXRefID == null) {
    return {}
  }
  return venueXRefID === sessionVenueXRefID
    ? { ...sessionParams, venue_xref_id: venueXRefID }
    : { venue_xref_id: venueXRefID }
}

export const trackEvent = (event: AnalyticsEvent, params: Record<string, unknown> = {}): void => {
  if (!publicConfig.ga4MeasurementId) {
    return
  }
  gtag('event', event, { ...analyticsContext(), ...params })
}

// Router paths (`asPath`) carry the query string, but the GTM triggers matched
// on path only and the regexes below are $-anchored — so a utm-tagged or
// fbclid-decorated URL would fall through to undefined. Marketing traffic is
// exactly what clicks the DGC banner, so strip the query and hash first.
const pathOnly = (url: string): string => url.split(/[?#]/)[0]

// The routes these events key off, from src/pages/[orderMethod]/[venueXRefID]/:
//
//   /{method}/{venue}                 venue landing
//   /{method}/{venue}/menu[/...]      view_menu     (item deep links sit under here)
//   /{method}/{venue}/checkout        view_checkout
//   /{method}/{venue}/{orderXRefID}   order_placed  (UUID, and must be the last segment)
//
// GTM matched `{{Page URL}} CONTAINS 'menu'` / `'checkout'`, which would also fire
// on any future route with those words anywhere in it. Anchoring to the section
// segment removes that drift while keeping every URL that fires today firing --
// the menu-item deep links included, which a naive `=== 'menu'` would have
// dropped. `method` is matched case-insensitively because OrderMethod's *values*
// are uppercase (PICKUP/DELIVERY) and only the call sites lowercase them.
interface OORoute {
  venueXRefID: string
  section?: string
  sectionIsLast: boolean
}

const parseOORoute = (url: string): OORoute | undefined => {
  const segments = pathOnly(url).split('/').filter(Boolean)
  const [method, venueXRefID, section] = segments
  if (method == null || !/^(pickup|delivery)$/i.test(method) || venueXRefID == null) {
    return undefined
  }
  return { venueXRefID, section, sectionIsLast: segments.length <= 3 }
}

// GTM's order-confirmation trigger matched the venue segment as `[0-9]+`.
// Consumer venue IDs are 3-6 digit integers today, but venueXRefID is a string
// and alphanumeric venue identifiers already exist elsewhere on the platform, so
// that is a convention rather than a guarantee -- and a miss here is a silently
// unlogged order_placed, not a visible error. The order ID is what identifies a
// confirmation page, so the venue segment is left unconstrained.
const ORDER_XREF_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const ooPageOf = (route: OORoute | undefined): 'menu' | 'checkout' | 'order_placed' | undefined => {
  if (route?.section == null) {
    return undefined
  }
  if (route.section === 'menu') {
    return 'menu'
  }
  if (route.section === 'checkout') {
    return 'checkout'
  }
  if (route.sectionIsLast && ORDER_XREF_ID.test(route.section)) {
    return 'order_placed'
  }
  return undefined
}

// Port of the GTM `oo_page` custom-JS variable (path -> page name), sent as the
// dgc_banner_cta event's oo_page param.
export const getOOPage = (path: string): 'menu' | 'checkout' | 'order_placed' | undefined =>
  ooPageOf(parseOORoute(path))

// The GTM container fired these off pageview + history-change triggers.
export const trackRouteView = (url: string): void => {
  const page = getOOPage(url)
  if (page === 'order_placed') {
    trackEvent('order_placed')
  } else if (page === 'checkout') {
    trackEvent('view_checkout')
  } else if (page === 'menu') {
    trackEvent('view_menu')
  }
}
